/**
 * Snapshot de la franja hero de Finanzas (`/finances`).
 *
 * Producto: en ≤10 s el dueño ve neto, vs mes anterior, top medio de pago y
 * señal de anomalía (#14: `financeAnomalyRules`).
 */

import { resolveFinanceAnomaly } from './financeAnomalyRules';
import type {
  FinanceAnomalyLevel,
  FinanceAnomalyReasonCode,
} from './financeAnomalyRules';
import type { CajaReporteFormaPago } from './metricsTypes';

function relativeChangePct(current: number, previous: number): number | null {
  if (previous === 0) {
    return null;
  }
  return ((current - previous) / previous) * 100;
}

export type FinanceHeroScope = 'network' | 'clinic';

/** @deprecated Prefer `FinanceAnomalyLevel` from financeAnomalyRules. */
export type FinanceHeroAnomalyLevel = FinanceAnomalyLevel;

export interface FinanceHeroTopMethod {
  /** Nombre de forma de pago; `null` = sin asignar. */
  label: string | null;
  /** Participación sobre ingresos del periodo (0–100). */
  sharePct: number;
  ingresos: number;
}

export interface FinanceHeroCashTotals {
  neto: number;
  ingresos: number;
  egresos: number;
  movimientos: number;
  porFormaPago?: CajaReporteFormaPago[];
}

export interface FinanceHeroSnapshot {
  scope: FinanceHeroScope;
  neto: number;
  ingresos: number;
  egresos: number;
  movimientos: number;
  /** Δ % MoM del neto; `null` si no hay base comparable. */
  vsPreviousPct: number | null;
  /** Δ absoluto MoM del neto; `null` si no hay periodo anterior. */
  vsPreviousAbs: number | null;
  topMethod: FinanceHeroTopMethod | null;
  anomaly: FinanceAnomalyLevel;
  anomalyPrimaryReason: FinanceAnomalyReasonCode;
  anomalyReasons: FinanceAnomalyReasonCode[];
  /** Δ % ingresos MoM (para copy de razón). */
  revenueDeltaPct: number | null;
  /** Datos de mix si la razón primaria o secundaria lo requiere. */
  anomalyMix?: {
    methodLabel: string | null;
    sharePct: number;
    shareDeltaPp: number;
  };
  /** Sin movimientos en el periodo (neto 0 + empty amable). */
  empty: boolean;
}

export interface BuildFinanceHeroSnapshotInput {
  scope: FinanceHeroScope;
  current: FinanceHeroCashTotals;
  previous: Pick<FinanceHeroCashTotals, 'neto' | 'ingresos' | 'movimientos' | 'porFormaPago'> | null;
}

/** Elige el medio con más ingresos; `null` si no hay desglose o ingresos = 0. */
export function pickTopPaymentMethod(
  porFormaPago: CajaReporteFormaPago[] | undefined,
  ingresosTotal: number
): FinanceHeroTopMethod | null {
  if (!porFormaPago?.length || ingresosTotal <= 0) {
    return null;
  }
  const top = [...porFormaPago].sort((a, b) => b.ingresos - a.ingresos)[0];
  if (!top || top.ingresos <= 0) {
    return null;
  }
  return {
    label: top.formaPago,
    sharePct: (top.ingresos / ingresosTotal) * 100,
    ingresos: top.ingresos,
  };
}

export function buildFinanceHeroSnapshot(input: BuildFinanceHeroSnapshotInput): FinanceHeroSnapshot {
  const { scope, current, previous } = input;
  const empty = current.movimientos === 0;
  const vsPreviousPct =
    empty || previous === null ? null : relativeChangePct(current.neto, previous.neto);
  const vsPreviousAbs = empty || previous === null ? null : current.neto - previous.neto;

  const anomaly = resolveFinanceAnomaly({
    current: {
      ingresos: current.ingresos,
      neto: current.neto,
      movimientos: current.movimientos,
      porFormaPago: current.porFormaPago,
    },
    previous: previous
      ? {
          ingresos: previous.ingresos,
          neto: previous.neto,
          movimientos: previous.movimientos ?? 0,
          porFormaPago: previous.porFormaPago,
        }
      : null,
    evaluatePaymentMix: scope === 'clinic',
  });

  return {
    scope,
    neto: current.neto,
    ingresos: current.ingresos,
    egresos: current.egresos,
    movimientos: current.movimientos,
    vsPreviousPct,
    vsPreviousAbs,
    topMethod: pickTopPaymentMethod(current.porFormaPago, current.ingresos),
    anomaly: anomaly.level,
    anomalyPrimaryReason: anomaly.primaryReason,
    anomalyReasons: anomaly.reasons,
    revenueDeltaPct: anomaly.revenueDeltaPct,
    anomalyMix: anomaly.mix,
    empty,
  };
}
